import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  createSubscription, 
  getMySubscription, 
  cancelSubscription,
  getSubscriptionPlans 
} from '../api/subscriptionApi';
import { getApiErrorMessage } from '../utils/apiError';
import '../styles/Subscription.css';

const Subscription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [processing, setProcessing] = useState(false);
  
  // Check if coming from User Portal
  const isUserPortal = localStorage.getItem('userPortalAccessToken') !== null;

  useEffect(() => {
    fetchMySubscription();
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await getSubscriptionPlans();
      setPlans(response.data || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchMySubscription = async () => {
    try {
      setLoading(true);
      const response = await getMySubscription();
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (processing) return;

    try {
      setProcessing(true);
      const plan = plans.find(p => p.type === selectedPlan);
      
      await createSubscription({
        subscriptionType: selectedPlan,
        amount: plan.price,
        currency: plan.currency
      });

      alert('Subscription created successfully! You now have access to all video content.');
      fetchMySubscription();
      
      // Redirect back to user portal after successful subscription
      setTimeout(() => {
        navigate('/user-portal');
      }, 1500);
    } catch (error) {
      console.error('Error creating subscription:', error);
      alert(getApiErrorMessage(error, 'Failed to create subscription. Please try again.'));
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium videos.')) {
      return;
    }

    try {
      setProcessing(true);
      await cancelSubscription(subscription.id);
      alert('Subscription cancelled successfully');
      fetchMySubscription();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert(getApiErrorMessage(error, 'Failed to cancel subscription. Please try again.'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="subscription-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (subscription && subscription.status === 'active') {
    return (
      <div className="subscription-container">
        <div className="subscription-header">
          <button className="back-button" onClick={() => navigate(isUserPortal ? '/user-portal' : '/Dashboard')}>
            ← Back
          </button>
          <h1>My Subscription</h1>
        </div>

        <div className="active-subscription-card">
          <div className="subscription-badge">
            <span>Active</span>
          </div>
          
          <div className="subscription-details">
            <h2>{subscription.subscriptionType.charAt(0).toUpperCase() + subscription.subscriptionType.slice(1)} Plan</h2>
            <div className="subscription-info">
              <div className="info-item">
                <span className="label">Status:</span>
                <span className="value status-active">{subscription.status}</span>
              </div>
              <div className="info-item">
                <span className="label">Started:</span>
                <span className="value">{new Date(subscription.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div className="info-item">
                <span className="label">Expires:</span>
                <span className="value">{new Date(subscription.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div className="info-item">
                <span className="label">Amount Paid:</span>
                <span className="value price">₹{subscription.amount}</span>
              </div>
            </div>

            <div className="subscription-benefits">
              <h3>Your Benefits</h3>
              <ul>
                <li>Access to all trainer videos</li>
                <li>Unlimited viewing</li>
                <li>HD Quality streaming</li>
                <li>Priority support</li>
              </ul>
            </div>

            <div className="subscription-actions">
              <button 
                onClick={() => navigate('/user-portal')}
                className="primary-button"
              >
                Watch Videos
              </button>
              <button 
                onClick={handleCancelSubscription}
                className="cancel-button"
                disabled={processing}
              >
                {processing ? 'Processing...' : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-container">
      <div className="subscription-header">
        <button className="back-button" onClick={() => navigate(isUserPortal ? '/user-portal' : '/Dashboard')}>
          ← Back
        </button>
        <h1>Choose Your Plan</h1>
        <p className="subtitle">Subscribe to access videos from all trainers</p>
      </div>

      <div className="plans-grid">
        {plans.map((plan) => (
          <div 
            key={plan.type}
            className={`plan-card ${selectedPlan === plan.type ? 'selected' : ''} ${plan.type === 'yearly' ? 'popular' : ''}`}
            onClick={() => setSelectedPlan(plan.type)}
          >
            {plan.type === 'yearly' && <div className="popular-badge">Best Value</div>}
            {plan.type === 'lifetime' && <div className="popular-badge premium">Premium</div>}
            
            <div className="plan-header">
              <h3>{plan.name}</h3>
              {plan.savings && <span className="savings">{plan.savings}</span>}
            </div>

            <div className="price-section">
              <div className="price">₹{plan.price}</div>
              <div className="duration">{plan.duration}</div>
            </div>

            <ul className="features-list">
              {plan.features.map((feature, index) => (
                <li key={index}>
                  <span className="check-icon">✓</span>
                  {feature}
                </li>
              ))}
            </ul>

            <div className="selection-indicator">
              <div className={`radio ${selectedPlan === plan.type ? 'checked' : ''}`}>
                {selectedPlan === plan.type && <div className="radio-dot"></div>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="subscribe-section">
        <div className="subscribe-info">
        <p className="info-text">
          <strong>Note:</strong> 
          {/* Payment gateway integration is coming soon. */} Click "Subscribe Now" to activate your subscription.
        </p>
        </div>
        
        <button 
          onClick={handleSubscribe}
          className="subscribe-button"
          disabled={processing || !selectedPlan}
        >
          {processing ? 'Processing...' : `Subscribe to ${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan`}
        </button>

        {/* <div className="payment-placeholder">
          <p>Payment gateway integration coming soon</p>
        </div> */}
      </div>

      <div className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>Can I cancel anytime?</h4>
            <p>Yes, you can cancel your subscription at any time from your subscription page.</p>
          </div>
          <div className="faq-item">
            <h4>What happens after I subscribe?</h4>
            <p>You'll get instant access to all video content from all trainers on the platform.</p>
          </div>
          <div className="faq-item">
            <h4>Do I need to pay for my trainer's videos?</h4>
            <p>No! Videos from your own trainer are always free. Subscription is only for accessing other trainers' content.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;

